import Store from '@ember-data/store';
import Service, { service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import { cached, tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import {
  type AppliedPropertyFilter,
  type DistanceFromVal,
  type GiftHistoryVal,
  type Operation,
  OperationFilters,
  type PropertyFilterInfo,
  type PropertyType,
} from 'donor-elf-web/lib/filter-utils-new';
import numberUtils from 'donor-elf-web/lib/number-utils';
import { queryWithPages } from 'donor-elf-web/lib/store-utils';
import ContactModel from 'donor-elf-web/models/contact';
import type { PropertyUserSettingItem } from 'donor-elf-web/models/contact-custom-property-user-setting';
import GeocodeModel from 'donor-elf-web/models/geocode';
import fuzzysort from 'fuzzysort';
import { getDistance } from 'geolib';
import type { GeolibInputCoordinates } from 'geolib/es/types';
import moment from 'moment';
import CampaignService from './campaign-service';
import FundsService from './funds-service';
import Settings from './settings';

export type ContactCustomPropertyType = 's' | 'n' | 'b' | 'c' | 'l';

export let PropertyTypeLabels: Record<ContactCustomPropertyType, string> = {
  s: 'Text',
  n: 'Number',
  b: 'Yes / No',
  c: 'Currency',
  l: 'Select from List',
};

export interface ContactCustomPropertyInfoSettings {
  single_selection?: boolean;
  cant_edit_options?: boolean;
  cant_delete?: boolean;
}
export interface ContactCustomPropertyInfo {
  l: string;
  r?: boolean;
  t: ContactCustomPropertyType;
  o?: string[];
  s?: ContactCustomPropertyInfoSettings;
  isGroupsProperty?: boolean; // only used for the "Groups" property when we want to include it in with the rest of the custom properties
}

export function isGroupsContactCustomPropertyInfo(
  info: ContactCustomPropertyInfo,
): boolean {
  return info.l === 'Groups';
}

export interface ContactIdsAndGroupsOptions {
  label: string;
  val?: any;
  cityAndState?: string;
  searchLabel?: string;
  isGroup?: boolean;
}

interface ContactIndexByStartsWith {
  [index: string]: {
    [word: string]: ContactModel[];
  };
}

export type ContactCustomPropertyVisibility = 'show' | 'hide-if-blank' | 'hide';

type ContactCustomPropertyFilterArgs = {
  operation?: Operation;
  val: any;
  contacts: ContactModel[];
};

type ContactCustomPropertyFilterFunction = (
  args: ContactCustomPropertyFilterArgs,
) => ContactModel[];

const ContactCustomPropertyTypeToFilterPropertyType: Record<
  ContactCustomPropertyType,
  PropertyType
> = {
  s: 'string',
  n: 'number',
  b: 'boolean',
  c: 'number',
  l: 'list',
};

export interface ContactPropertyFilterInfo extends PropertyFilterInfo {
  custom: {
    filter: (args: ContactCustomPropertyFilterArgs) => ContactModel[];
    preFilterQuery?: (filter: AppliedPropertyFilter) => Promise<void>; // async query to retrieve the contact ids from the server
    includeOnAnyReport: boolean;
  };
}

const ContactGiftHistoryCache = {
  generateCacheKey(operation: Operation, val: GiftHistoryVal): string {
    return `${moment().format('YYYYMMDD')}-${val.val}-${val.valType}-${operation}-${val.from}-${val.to}`;
  },
  cache: {} as { [cacheKey: string]: { [contactId: string]: boolean } },
};

const GeocodeCache: { [location: string]: GeocodeModel } = {};

class ContactFuzzySort {
  constructor(public contact: ContactModel) {}

  @cached
  get searchString() {
    let searchStrings = [this.contact.name];
    if (this.contact.namePartsDifferentThanDisplayName) {
      if (isPresent(this.contact.firstName)) {
        searchStrings.push(this.contact.firstName!);
      }
      if (isPresent(this.contact.spouseFirstName)) {
        searchStrings.push(this.contact.spouseFirstName!);
      }
      if (isPresent(this.contact.lastName)) {
        searchStrings.push(this.contact.lastName!);
      }
      if (isPresent(this.contact.spouseLastName)) {
        searchStrings.push(this.contact.spouseLastName!);
      }
    }
    if (isPresent(this.contact.city)) {
      searchStrings.push(this.contact.city!);
    }
    for (let customProperty of this.contact.custom?.properties || []) {
      if (customProperty.t == null || customProperty.t === 's') {
        searchStrings.push(customProperty.v);
      }
    }
    return searchStrings.join(' ');
  }

  @cached
  get searchStringPrepared(): Fuzzysort.Prepared {
    return fuzzysort.prepare(this.searchString);
  }

  @cached
  get emailPrepared() {
    return fuzzysort.prepare(
      this.contact.emails?.data?.map((x) => x.v)?.join(' ') || '',
    );
  }
}

export default class ContactsCache extends Service {
  @service declare campaignService: CampaignService;
  @service declare fundsService: FundsService;
  @service declare settings: Settings;
  @service declare store: Store;

  // array of contact ids to cycle through when clicking
  // the next/previous buttons on the contact detail page
  // format: ['1234', '2', '223']
  nextPreviousContactIds: string[] = [];
  @tracked contacts: ContactModel[] = [];

  @cached
  get contactsById(): { [contactId: string]: ContactModel } {
    let index: { [contactId: string]: ContactModel } = {};
    for (let contact of this.contacts) {
      index[contact.id] = contact;
    }
    return index;
  }

  @tracked contactsSortBy = ['nameLowerCase:asc'];

  @cached
  get sortedContacts() {
    return sortByProperties<ContactModel>(this.contacts, this.contactsSortBy);
  }

  // Duct tape way to break the cache for `sortedGroups` and `groupContacts`
  // You have to manually increment `groupsCacheIndex` to
  // recalculate dependent computed properties
  @tracked groupsCacheIndex = 0;

  // Duct tape way to break the cache for `customPropertiesByLowercaseLabel`
  // You have to manually increment `customPropertiesCacheIndex` to
  // recalculate dependent computed properties
  @tracked customPropertiesCacheIndex = 0;

  @cached
  get sortedGroups() {
    // access the groupsCacheIndex so the cached is cleared when it changes
    // @ts-ignore
    let _groupsCacheIndex = this.groupsCacheIndex;

    let groups: any = {};
    this.settings.fundSettings?.defaultGroups?.forEach((group: string) => {
      groups[group] = true;
    });
    this.contacts.forEach((contact) => {
      contact.groups?.forEach((group) => {
        groups[group] = true;
      });
    });
    return Object.keys(groups).sort();
  }

  @cached
  get groupContacts(): { [groupName: string]: { contacts: ContactModel[] } } {
    let groupContacts: any = {};
    this.sortedGroups.forEach((group) => {
      groupContacts[group] = { contacts: [] };
    });
    this.contacts.forEach((contact) => {
      contact.groups?.forEach((group) => {
        groupContacts[group].contacts.push(contact);
      });
    });
    return groupContacts;
  }

  getById(contactId: string | undefined): ContactModel | undefined {
    return this.contactsById[contactId!];
  }

  get contactIdsAndGroupsOptions(): ContactIdsAndGroupsOptions[] {
    // access the groupsCacheIndex so the cached is cleared when it changes
    // @ts-ignore
    let _groupsCacheIndex = this.groupsCacheIndex;

    return this.sortedContacts
      .map((contact) => {
        return {
          label: contact.name!,
          val: `id:${contact.id}`,
          cityAndState: contact.cityAndState,
        } as ContactIdsAndGroupsOptions;
      })
      .concat(
        this.sortedGroups.map((group) => {
          return {
            label: group,
            val: `group:${group}`,
            searchLabel: `group: ${group.toLowerCase()}`,
            isGroup: true,
          } as ContactIdsAndGroupsOptions;
        }),
      )
      .concat([
        {
          label: 'All Contacts',
          val: 'all-contacts',
        } as ContactIdsAndGroupsOptions,
      ]);
  }

  addContact(contact: ContactModel) {
    this.contacts = [...this.contacts, contact];
  }

  updateCustomPropertyInfo(newCustomPropertyInfo: ContactCustomPropertyInfo) {
    let existingInfoIndex =
      this.settings.fundSettings!.contactCustomInfo?.properties?.findIndex(
        (x) => x.l === newCustomPropertyInfo.l,
      );
    if (existingInfoIndex == null) {
      existingInfoIndex = -1;
    }
    if (existingInfoIndex >= 0) {
      let existingInfo =
        this.settings.fundSettings!.contactCustomInfo!.properties![
          existingInfoIndex!
        ];
      // only update the info if it's different
      if (
        JSON.stringify(existingInfo) !== JSON.stringify(newCustomPropertyInfo)
      ) {
        this.settings.fundSettings!.contactCustomInfo!.properties![
          existingInfoIndex!
        ] = newCustomPropertyInfo;
        // break the cache
        this.customPropertiesCacheIndex += 1;
      }
    } else {
      if (this.settings.fundSettings!.contactCustomInfo == null) {
        this.settings.fundSettings!.contactCustomInfo = { properties: [] };
      }
      this.settings.fundSettings!.contactCustomInfo!.properties!.push(
        newCustomPropertyInfo,
      );
      // break the cache
      this.customPropertiesCacheIndex += 1;
    }
  }

  // does NOT include the "Groups" custom property info
  @cached
  get customProperties(): ContactCustomPropertyInfo[] {
    // access the customPropertiesCacheIndex so the cached is cleared when it changes
    // @ts-ignore
    let _customPropertiesCacheIndex = this.customPropertiesCacheIndex;
    return this.settings.fundSettings?.contactCustomInfo?.properties || [];
  }

  // TODO: fix the spelling mistake on the function name
  @cached
  get customPropertiesByLowecaseLabel(): {
    [label: string]: ContactCustomPropertyInfo;
  } {
    let info: { [label: string]: ContactCustomPropertyInfo } = {};
    for (let x of this.customProperties) {
      info[x.l.toLowerCase()] = x;
    }
    return info;
  }

  customPropertyLabelIsReadonly(label: string) {
    return (
      this.customPropertiesByLowecaseLabel[label.toLowerCase()]?.r || false
    );
  }

  get groupsContactCustomPropertyInfo(): ContactCustomPropertyInfo {
    return {
      l: 'Groups',
      t: 'l',
      o: this.sortedGroups,
      s: { single_selection: false },
      isGroupsProperty: true,
    };
  }

  // includes "Groups" custom property
  @cached
  get sortedCustomPropertiesForDetailPage(): ContactCustomPropertyInfo[] {
    let customProperties = [
      this.groupsContactCustomPropertyInfo,
      ...this.customProperties,
    ];
    if (
      (this.settings.contactCustomPropertyUserSetting?.detailPage?.length ||
        0) > 0
    ) {
      let genericCustomProperties = [...customProperties];
      customProperties = [];
      let customProperty: ContactCustomPropertyInfo | undefined;
      for (let x of this.settings.contactCustomPropertyUserSetting!
        .detailPage) {
        if (
          (customProperty = genericCustomProperties.find((y) => y.l === x.l))
        ) {
          customProperties.push(customProperty);
        }
      }
      // add any missing custom properties
      for (let x of genericCustomProperties) {
        if (!customProperties.includes(x)) {
          customProperties.push(x);
        }
      }
      // TODO: do we save the missing custom properties to `detailPage` settings here?
      // TODO: do we remove any detailPage custom properties that are no longer in `customProperties`?
    }
    return customProperties;
  }

  async updateCustomPropertyForDetailPageVisibility(
    label: string,
    visibility: ContactCustomPropertyVisibility,
  ) {
    let { contactCustomPropertyUserSetting } = this.settings;
    if (contactCustomPropertyUserSetting == null) {
      contactCustomPropertyUserSetting = this.store.createRecord(
        'contact-custom-property-user-setting',
        {
          detailPage: [],
        },
      );
    }
    let updatedDetailPage: PropertyUserSettingItem[] = [];

    if (contactCustomPropertyUserSetting.detailPage?.length === 0) {
      updatedDetailPage =
        this.defaultContactCustomPropertyUserSettingForDetailPage;
    } else if (
      contactCustomPropertyUserSetting.detailPage.map((x) => x.l).join(',') !==
      this.sortedCustomPropertiesForDetailPage.map((x) => x.l).join(',')
    ) {
      // if detailPage doesn't equal the current sorted custom properties, update it
      updatedDetailPage = this.sortedCustomPropertiesForDetailPage.map((x) => {
        return (
          contactCustomPropertyUserSetting.detailPage.find(
            (y) => y.l === x.l,
          ) || { l: x.l }
        );
      });
    } else {
      updatedDetailPage = [...contactCustomPropertyUserSetting.detailPage];
    }

    // update the visibility
    let visibilityChanged = false;
    let item = updatedDetailPage.find((x) => x.l === label) || { l: label };
    if (visibility === 'show') {
      if (item.show !== true) {
        visibilityChanged = true;
        item.show = true;
        if (item.hide) {
          delete item.hide;
        }
      }
    } else if (visibility === 'hide') {
      if (item.hide !== true) {
        visibilityChanged = true;
        item.hide = true;
        if (item.show) {
          delete item.show;
        }
      }
    } else {
      if (item.show || item.hide) {
        visibilityChanged = true;
        if (item.show) {
          delete item.show;
        }
        if (item.hide) {
          delete item.hide;
        }
      }
    }
    if (!visibilityChanged) {
      return;
    }

    // update the order of properties
    let updatedDetailPageWithoutItem = updatedDetailPage.filter(
      (x) => x !== item,
    );
    let alwaysShowItems = updatedDetailPageWithoutItem.filter(
      (x) => x.show === true,
    );
    let hideIfBlankItems = updatedDetailPageWithoutItem.filter(
      (x) => x.show !== true && x.hide !== true,
    );
    let alwaysHideItems = updatedDetailPageWithoutItem.filter(
      (x) => x.hide === true,
    );

    // add the changed property to the end of its new visibility section
    if (item.show) {
      alwaysShowItems.push(item);
    } else if (item.hide) {
      alwaysHideItems.push(item);
    } else {
      hideIfBlankItems.push(item);
    }

    let sortedDetailPage = [
      ...alwaysShowItems,
      ...hideIfBlankItems,
      ...alwaysHideItems,
    ];
    // no longer show hidden properties if there are no hidden properties
    if (
      hideIfBlankItems.length === 0 &&
      alwaysHideItems.length === 0 &&
      this.showHiddenCustomPropertiesOnDetailPage
    ) {
      this.showHiddenCustomPropertiesOnDetailPage = false;
    }

    contactCustomPropertyUserSetting.detailPage = sortedDetailPage;
    this.settings.contactCustomPropertyUserSetting =
      contactCustomPropertyUserSetting;
    await contactCustomPropertyUserSetting.save();
  }

  @cached
  get customPropertyUserSettingForDetailPage(): {
    [label: string]: PropertyUserSettingItem;
  } {
    let settings: { [label: string]: PropertyUserSettingItem } = {};
    let detailPage = this.settings.contactCustomPropertyUserSetting?.detailPage;
    if (detailPage == null || detailPage.length === 0) {
      detailPage = this.defaultContactCustomPropertyUserSettingForDetailPage;
    }
    for (let x of detailPage) {
      settings[x.l] = x;
    }
    return settings;
  }

  get defaultContactCustomPropertyUserSettingForDetailPage(): PropertyUserSettingItem[] {
    return [this.groupsContactCustomPropertyInfo, ...this.customProperties].map(
      (x) => {
        let item = { l: x.l } as PropertyUserSettingItem;
        // Groups and Status default to always show
        if (item.l === 'Groups' || item.l === 'Status') {
          item.show = true;
        }
        return item;
      },
    );
  }

  @tracked showHiddenCustomPropertiesOnDetailPage = false;

  @cached
  get availablePropertyFiltersForContacts(): ContactPropertyFilterInfo[] {
    let filterInfos: ContactPropertyFilterInfo[] = [];
    filterInfos.push({
      propertyName: 'contactNames',
      label: 'Name Fields',
      propertyType: 'string',
      availableOperations: ['stringContains'],
      custom: {
        filter: (args: ContactCustomPropertyFilterArgs): ContactModel[] => {
          let searchWords: string[] =
            args.val?.toLowerCase()?.split(/\s+/) || [];
          let matchInfo = args.contacts
            .map((contact) => {
              return {
                contact,
                matches: searchWords
                  .map((word) => contact.allNamesLowerCase.indexOf(word))
                  .filter((x) => x >= 0)
                  .sort(),
              };
            })
            .filter((info) => {
              return info.matches.length > 0;
            });

          if (
            searchWords.length > 1 &&
            matchInfo.some((x) => x.matches.length > 1)
          ) {
            return matchInfo
              .filter((x) => x.matches.length > 1)
              .map((x) => x.contact);
          } else {
            return matchInfo.map((x) => x.contact);
          }
        },
        includeOnAnyReport: true,
      },
    });
    if (this.sortedGroups.length > 0) {
      filterInfos.push({
        propertyName: 'contactGroups',
        label: 'Groups',
        propertyType: 'list',
        listOptions: this.sortedGroups,
        custom: {
          filter: (args: ContactCustomPropertyFilterArgs): ContactModel[] => {
            let filteredVals = args.val as string[];
            if (args.operation === 'any') {
              return args.contacts.filter((contact) => {
                return contact.groups?.some((x) => filteredVals.includes(x));
              });
            } else if (args.operation === 'none') {
              return args.contacts.filter((contact) => {
                return !contact.groups?.some((x) => filteredVals.includes(x));
              });
            } else if (args.operation === 'all') {
              return args.contacts.filter((contact) => {
                return filteredVals.every((x) => contact.groups?.includes(x));
              });
            } else if (args.operation === 'absent') {
              return args.contacts.filter((contact) => {
                return !contact.hasGroups;
              });
            } else if (args.operation === 'present') {
              return args.contacts.filter((contact) => {
                return contact.hasGroups;
              });
            } else {
              // it shouldn't ever get here
              return args.contacts;
            }
          },
          includeOnAnyReport: true,
        },
      });
    }
    filterInfos.push({
      propertyName: 'contactGiftHistory',
      label: 'Has Given',
      propertyType: 'giftHistory',
      custom: {
        preFilterQuery: async (
          filter: AppliedPropertyFilter,
        ): Promise<void> => {
          let filterVal = filter.val as GiftHistoryVal;
          let cacheKey = ContactGiftHistoryCache.generateCacheKey(
            filter.operation!,
            filterVal,
          );
          if (ContactGiftHistoryCache.cache[cacheKey] == null) {
            let record = (
              await this.store.query('contact-filter', {
                filter: {
                  gift_history: {
                    val: filterVal.val,
                    val_type: filterVal.valType,
                    operation: filter.operation,
                    from: filterVal.from,
                    to: filterVal.to,
                  },
                },
              })
            ).slice()[0];
            ContactGiftHistoryCache.cache[cacheKey] = {};
            for (let contactId of record.contactIds) {
              ContactGiftHistoryCache.cache[cacheKey][contactId] = true;
            }
          }
        },
        filter: (args: ContactCustomPropertyFilterArgs): ContactModel[] => {
          let filterVal = args.val as GiftHistoryVal;
          let cacheKey = ContactGiftHistoryCache.generateCacheKey(
            args.operation!,
            filterVal,
          );
          return args.contacts.filter(
            (contact) => ContactGiftHistoryCache.cache[cacheKey][contact.id],
          );
        },
        includeOnAnyReport: true,
      },
    });
    if (
      this.campaignService.campaignsEnabled &&
      this.campaignService.campaigns.length > 0
    ) {
      // have to call is so the cache is correctly broken since it's not used
      // until the filter is actually used
      let breakTheCache = this.campaignService.campaignContacts;

      filterInfos.push({
        propertyName: 'campaignIds',
        label: 'Campaigns',
        propertyType: 'list',
        listOptions: this.campaignService.sortedCampaignSelectOptions,
        custom: {
          filter: (args: ContactCustomPropertyFilterArgs): ContactModel[] => {
            let allCampaignIds: string[] = [];
            for (let campaignId of args.val as string[]) {
              let campaign = this.campaignService.campaignsById[campaignId];
              if (campaign) {
                allCampaignIds = [
                  ...allCampaignIds,
                  ...campaign.idAndDescendantIds,
                ];
              }
            }
            const campaignIds = Array.from(new Set(allCampaignIds));

            switch (args.operation) {
              case 'any':
                return args.contacts.filter((contact) =>
                  this.campaignService.campaignContactsByContactId[
                    contact.id
                  ]?.some((c) => campaignIds.includes(c.campaignId)),
                );
              case 'all':
                return args.contacts.filter((contact) => {
                  let campaignContacts =
                    this.campaignService.campaignContactsByContactId[
                      contact.id
                    ];
                  return (
                    campaignContacts &&
                    campaignIds.every((id) =>
                      campaignContacts.some((c) => c.campaignId === id),
                    )
                  );
                });
              case 'none': // not in any
                return args.contacts.filter((contact) => {
                  let campaignContacts =
                    this.campaignService.campaignContactsByContactId[
                      contact.id
                    ];
                  return (
                    campaignContacts == null ||
                    !campaignIds.some((id) =>
                      campaignContacts.some((c) => c.campaignId === id),
                    )
                  );
                });
              case 'present':
                return args.contacts.filter((contact) => {
                  return (
                    (
                      this.campaignService.campaignContactsByContactId[
                        contact.id
                      ] || []
                    ).length > 0
                  );
                });
              case 'absent':
                return args.contacts.filter((contact) => {
                  return (
                    (
                      this.campaignService.campaignContactsByContactId[
                        contact.id
                      ] || []
                    ).length === 0
                  );
                });
              default:
                return args.contacts;
            }
          },
          includeOnAnyReport: true,
        },
      });
    }
    for (let customProperty of this.customProperties) {
      let valForContact = (contact: ContactModel): any => {
        let val = contact.customPropertiesByLabel[customProperty.l]?.v;
        if (val && customProperty.t === 's') {
          val = val.toLowerCase();
        }
        return val;
      };
      filterInfos.push({
        propertyName: `contact_custom_${customProperty.l}`,
        label: customProperty.l,
        propertyType:
          ContactCustomPropertyTypeToFilterPropertyType[
            customProperty.t || 's'
          ],
        listOptions: customProperty.o,
        listIsSingleSelection: customProperty.s?.single_selection || false,
        custom: {
          filter: (args: ContactCustomPropertyFilterArgs): ContactModel[] => {
            if (customProperty.t === 'l') {
              let filteredVals = args.val as string[];
              if (args.operation === 'any') {
                return args.contacts.filter((contact) => {
                  let contactVal = valForContact(contact);
                  return (
                    contactVal &&
                    filteredVals.some((x) => contactVal[x] === true)
                  );
                });
              } else if (args.operation === 'none') {
                return args.contacts.filter((contact) => {
                  let contactVal = valForContact(contact);
                  return (
                    contactVal == null ||
                    !filteredVals.some((x) => contactVal[x] === true)
                  );
                });
              } else if (args.operation === 'absent') {
                return args.contacts.filter((contact) => {
                  return OperationFilters.absent(valForContact(contact));
                });
              } else if (args.operation === 'present') {
                return args.contacts.filter((contact) => {
                  return OperationFilters.present(valForContact(contact));
                });
              } else {
                // (args.operation === 'all')
                return args.contacts.filter((contact) => {
                  let contactVal = valForContact(contact);
                  return (
                    contactVal &&
                    filteredVals.every((x) => contactVal[x] === true)
                  );
                });
              }
            } else if (args.operation === '=') {
              if (customProperty.t === 's') {
                let filterValLowerCase = args.val.toLowerCase();
                return args.contacts.filter((contact) => {
                  return OperationFilters.stringEquals(
                    filterValLowerCase,
                    valForContact(contact),
                  );
                });
              } else {
                let filterVal = args.val;
                if (customProperty.t === 'n' || customProperty.t === 'c') {
                  filterVal = Number(filterVal);
                }
                return args.contacts.filter((contact) => {
                  return valForContact(contact) === filterVal;
                });
              }
            } else if (args.operation === 'absent') {
              return args.contacts.filter((contact) => {
                return OperationFilters.absent(valForContact(contact));
              });
            } else if (args.operation === 'present') {
              return args.contacts.filter((contact) => {
                return OperationFilters.present(valForContact(contact));
              });
            } else if (args.operation === 'stringContains') {
              // only works for strings
              let filterValLowerCase = args.val.toLowerCase();
              return args.contacts.filter((contact) => {
                return OperationFilters.stringContains(
                  filterValLowerCase,
                  valForContact(contact),
                );
              });
            } else if (args.operation === '>') {
              let filterVal = Number(args.val);
              return args.contacts.filter((contact) => {
                return valForContact(contact) > filterVal;
              });
            } else if (args.operation === '>=') {
              let filterVal = Number(args.val);
              return args.contacts.filter((contact) => {
                return valForContact(contact) >= filterVal;
              });
            } else if (args.operation === '<') {
              let filterVal = Number(args.val);
              return args.contacts.filter((contact) => {
                return valForContact(contact) < filterVal;
              });
            } else if (args.operation === '<=') {
              let filterVal = Number(args.val);
              return args.contacts.filter((contact) => {
                return valForContact(contact) <= filterVal;
              });
            } else if (args.operation === '!=') {
              let filterVal = args.val;
              if (customProperty.t === 'n' || customProperty.t === 'c') {
                filterVal = Number(filterVal);
              }
              return args.contacts.filter((contact) => {
                return valForContact(contact) != filterVal;
              });
            } else {
              return args.contacts;
            }
          },
          includeOnAnyReport: true,
        },
      });
    }
    filterInfos.push({
      propertyName: 'contactLocation',
      label: 'Location',
      propertyType: 'distanceFrom',
      custom: {
        preFilterQuery: async (
          filter: AppliedPropertyFilter,
        ): Promise<void> => {
          let filterVal = filter.val as DistanceFromVal;
          if (GeocodeCache[filterVal.location] == null) {
            let geocodeRecord = (
              await this.store.query('geocode', {
                filter: {
                  address: filterVal.location,
                },
              })
            ).slice()[0];
            GeocodeCache[filterVal.location] = geocodeRecord;
          }
        },
        filter: (args: ContactCustomPropertyFilterArgs): ContactModel[] => {
          const filterVal = args.val as DistanceFromVal;
          const geocode = GeocodeCache[filterVal.location];
          if (geocode) {
            const range = numberUtils.milesToMeters(filterVal.distance);
            const fromCoords = {
              latitude: geocode.latitude,
              longitude: geocode.longitude,
            };
            return args.contacts.filter((contact) => {
              if ((contact.locationConfidence || 0) < 0.3) {
                return false;
              }

              const distance = getDistance(fromCoords, {
                latitude: contact.latitude,
                longitude: contact.longitude,
              } as GeolibInputCoordinates);

              return distance < range;
            });
          } else {
            return [];
          }
        },
        includeOnAnyReport: true,
      },
    });
    filterInfos.push({
      propertyName: 'contactPhones',
      label: 'Phone',
      propertyType: 'string',
      availableOperations: ['=', 'stringContains', 'present', 'absent'],
      custom: {
        filter: (args: ContactCustomPropertyFilterArgs): ContactModel[] => {
          if (args.operation === 'present') {
            return args.contacts.filter((contact) => {
              return contact.hasPhones;
            });
          } else if (args.operation === 'absent') {
            return args.contacts.filter((contact) => {
              return !contact.hasPhones;
            });
          } else if (args.operation === 'stringContains') {
            let valDigitsOnly = args.val.replace(/\D/g, '');
            return args.contacts.filter((contact) => {
              if (contact.hasPhones) {
                return contact.phones!.data!.some((x) =>
                  x.v.replace(/\D/g, '').includes(valDigitsOnly),
                );
              } else {
                return false;
              }
            });
          } else {
            // args.operation === '='
            let valDigitsOnly = args.val.replace(/\D/g, '');
            return args.contacts.filter((contact) => {
              if (contact.hasPhones) {
                return contact.phones!.data!.some(
                  (x) => x.v.replace(/\D/g, '') == valDigitsOnly,
                );
              } else {
                return false;
              }
            });
          }
        },
        includeOnAnyReport: false,
      },
    });
    filterInfos.push({
      propertyName: 'contactEmails',
      label: 'Email',
      propertyType: 'string',
      availableOperations: ['=', 'stringContains', 'present', 'absent'],
      custom: {
        filter: (args: ContactCustomPropertyFilterArgs): ContactModel[] => {
          if (args.operation === 'present') {
            return args.contacts.filter((contact) => {
              return contact.hasEmails;
            });
          } else if (args.operation === 'absent') {
            return args.contacts.filter((contact) => {
              return !contact.hasEmails;
            });
          } else if (args.operation === 'stringContains') {
            let valLowerCase = args.val.toLowerCase();
            return args.contacts.filter((contact) => {
              if (contact.hasEmails) {
                return contact.emails!.data!.some((x) =>
                  x.v.toLowerCase().includes(valLowerCase),
                );
              } else {
                return false;
              }
            });
          } else {
            // args.operation === '='
            let valLowerCase = args.val.toLowerCase();
            return args.contacts.filter((contact) => {
              if (contact.hasEmails) {
                return contact.emails!.data!.some(
                  (x) => x.v.toLowerCase() == valLowerCase,
                );
              } else {
                return false;
              }
            });
          }
        },
        includeOnAnyReport: false,
      },
    });
    filterInfos.push({
      propertyName: 'contactCity',
      label: 'City',
      propertyType: 'string',
      availableOperations: ['=', 'stringContains'],
      custom: {
        filter: (args: ContactCustomPropertyFilterArgs): ContactModel[] => {
          let valLowerCase = args.val.toLowerCase();
          if (args.operation === '=') {
            return args.contacts.filter(
              (contact) => contact.city?.toLowerCase() == valLowerCase,
            );
          } else {
            return args.contacts.filter((contact) =>
              contact.city?.toLowerCase()?.includes(valLowerCase),
            );
          }
        },
        includeOnAnyReport: false,
      },
    });
    filterInfos.push({
      propertyName: 'contactState',
      label: 'State',
      propertyType: 'string',
      availableOperations: ['=', 'stringContains'],
      custom: {
        filter: (args: ContactCustomPropertyFilterArgs): ContactModel[] => {
          let valLowerCase = args.val.toLowerCase();
          if (args.operation === '=') {
            return args.contacts.filter(
              (contact) => contact.state?.toLowerCase() == valLowerCase,
            );
          } else {
            return args.contacts.filter((contact) =>
              contact.state?.toLowerCase()?.includes(valLowerCase),
            );
          }
        },
        includeOnAnyReport: false,
      },
    });
    filterInfos.push({
      propertyName: 'contact_show_hidden',
      label: 'Show Hidden',
      propertyType: 'no-operation',
      custom: {
        filter: (args: ContactCustomPropertyFilterArgs): ContactModel[] => {
          // don't filter anything here
          return args.contacts;
        },
        includeOnAnyReport: false,
      },
    });

    return filterInfos;
  }

  _loadPromise?: Promise<ContactModel[]>;
  loadContacts({ forceLoad }: { forceLoad?: boolean } = {}) {
    if (forceLoad) {
      this._loadPromise = undefined;
    }
    if (this._loadPromise) {
      return this._loadPromise;
    }
    return (this._loadPromise = queryWithPages<ContactModel>({
      store: this.store,
      modelName: 'contact',
      pageSize: 250,
      // these queryOptions don't affect the filter but it includes the
      // fund id in the error report if this request times out so we know
      // what fund id it's having trouble loading contacts for
      queryOptions: {
        fund_id: this.fundsService.selectedFund?.id,
      },
    }).then((contacts) => {
      this.contacts = contacts;
      this.groupsCacheIndex += 1;
      this.nextPreviousContactIds = this.sortedContacts.map((x) => x.id);
      return contacts;
    }));
  }

  async reloadSpecificContacts(contactIds: string[]): Promise<ContactModel[]> {
    if (contactIds.length > 250) {
      return this.loadContacts({ forceLoad: true });
    } else {
      let reloadedContacts = queryWithPages<ContactModel>({
        store: this.store,
        modelName: 'contact',
        pageSize: 250,
        queryOptions: {
          filter: {
            id: contactIds,
          },
        },
      });
      this.contacts = this.contacts; // break the cache
      return reloadedContacts;
    }
  }

  // loads new contacts added or updated today
  async loadModifiedContacts(): Promise<void> {
    let modifiedContacts = await queryWithPages<ContactModel>({
      store: this.store,
      modelName: 'contact',
      pageSize: 250,
      // these queryOptions don't affect the filter but it includes the
      // fund id in the error report if this request times out so we know
      // what fund id it's having trouble loading contacts for
      queryOptions: {
        fund_id: this.fundsService.selectedFund?.id,
        filter: { updated_today: true },
      },
    });
    // add the new contacts
    let newContacts = modifiedContacts.filter(
      (x) => this.contactsById[x.id] == null,
    );
    this.contacts = [...this.contacts, ...newContacts];
    this.groupsCacheIndex += 1;
  }

  removeContactId(contactId: string) {
    this.contacts = this.contacts.filter((x) => x.id !== contactId);
  }

  // Creates a contact and adds it to the contacts list
  // It does NOT save the contact though
  createContact(initialValues = {}): ContactModel {
    // don't need to explicitly add the new record to `contacts` because it's already
    // a RecordArray from the store, so the store will do it for us
    return this.store.createRecord('contact', initialValues);
  }

  unload() {
    this._loadPromise = undefined;
    this.contacts = [];
  }

  // @cached
  // get indexByStartsWith(): ContactIndexByStartsWith {
  //   let startsWithIndex: ContactIndexByStartsWith = {};
  //   this.sortedContacts.forEach(contact => {
  //     let names = (contact.name || '').split(' ');
  //     names.forEach(nameWord => {
  //       if (nameWord.length > 1) {
  //         this.addContactToStartsWithIndex(startsWithIndex, contact, nameWord);
  //       }
  //     });

  //     if (contact.namePartsDifferentThanDisplayName) {
  //       if (isPresent(contact.firstName)) {
  //         this.addContactToStartsWithIndex(startsWithIndex, contact, contact.firstName!);
  //       }
  //       if (isPresent(contact.spouseFirstName)) {
  //         this.addContactToStartsWithIndex(startsWithIndex, contact, contact.spouseFirstName!);
  //       }
  //       if (isPresent(contact.lastName)) {
  //         this.addContactToStartsWithIndex(startsWithIndex, contact, contact.lastName!);
  //       }
  //       if (isPresent(contact.spouseLastName)) {
  //         this.addContactToStartsWithIndex(startsWithIndex, contact, contact.spouseLastName!);
  //       }
  //     }

  //     let cityWords = (contact.city || '').split(' ');
  //     cityWords.forEach(cityWord => {
  //       if (cityWord.length > 1) {
  //         this.addContactToStartsWithIndex(startsWithIndex, contact, cityWord);
  //       }
  //     });

  //     contact.emails?.data?.forEach(item => {
  //       if (item.v.length > 1) {
  //         this.addContactToStartsWithIndex(startsWithIndex, contact, item.v);
  //       }
  //     });

  //     contact.custom?.properties?.forEach(item => {
  //       if (item.t == null || item.t === 's') {
  //         let valueWords = item.v?.toString()?.split(' ') || [];
  //         valueWords.forEach((word:string) => {
  //           if (word.length > 1) {
  //             this.addContactToStartsWithIndex(startsWithIndex, contact, word);
  //           }
  //         });
  //       }
  //     });
  //   });

  //   return startsWithIndex;
  // }

  // addContactToStartsWithIndex(startsWithIndex: ContactIndexByStartsWith, contact: ContactModel, word: string) {
  //   let scrubbedWord = normalizeAccentCharacters(word).toLowerCase();
  //   let startsWith   = normalizeAccentCharacters(word).toLowerCase().slice(0, 2);
  //   startsWithIndex[startsWith] = (startsWithIndex[startsWith] || {});
  //   startsWithIndex[startsWith][scrubbedWord] = (startsWithIndex[startsWith][scrubbedWord] || []);
  //   startsWithIndex[startsWith][scrubbedWord].push(contact);
  // }

  @cached
  get contactFuzzySorts() {
    return this.sortedContacts.map((contact) => {
      return new ContactFuzzySort(contact);
    });
  }

  search(
    query: string,
    options: { limit?: number; threshold?: number } = {},
  ): ContactModel[] {
    if (isEmpty(query)) {
      if (options.limit) {
        return this.sortedContacts.slice(0, options.limit);
      } else {
        return this.sortedContacts;
      }
    }

    if (options.threshold == null) {
      options.threshold = -5000;
    }
    let fuzzysortOptions: Fuzzysort.KeyOptions = {
      threshold: options.threshold,
      key: 'searchStringPrepared',
    };
    if (query.includes('@')) {
      fuzzysortOptions.key = 'emailPrepared';
    }
    if (options?.limit) {
      fuzzysortOptions.limit = options.limit;
    }

    return fuzzysort
      .go(query, this.contactFuzzySorts, fuzzysortOptions)
      .map((fuzzyResult: Fuzzysort.KeyResult<ContactFuzzySort>) => {
        return fuzzyResult.obj.contact;
      });

    // this is the old search method that uses the indexByStartsWith
    // we migrated to fuzzysort to get better search results
    // but are keeping this here to confirm the new search results
    // are better than the old search results

    // let queryWords = normalizeAccentCharacters(query || '').toLowerCase().split(' ').filter(item => {
    //   return item !== '';
    // });
    // if (queryWords.length === 0) {
    //   return this.sortedContacts;
    // }

    // let indexByStartsWith = this.indexByStartsWith;
    // let matchedContactsById: any = {};
    // let contactIdsMatchCount: any = {};

    // queryWords.forEach(queryWord => {
    //   if (queryWord.length > 1) {
    //     let startsWithMatches = indexByStartsWith[queryWord.slice(0, 2)];
    //     if (startsWithMatches) {
    //       Object.keys(startsWithMatches).forEach(indexWord => {
    //         if (indexWord.indexOf(queryWord) === 0) {
    //           startsWithMatches[indexWord].forEach(contact => {
    //             matchedContactsById[contact.id] = contact;
    //             contactIdsMatchCount[contact.id] = (contactIdsMatchCount[contact.id] || 0) + 1;
    //           });
    //         }
    //       });
    //     }
    //   }
    // });

    // let highestMatchCount = 0;
    // let contactsByMatchCount: any = {};
    // Object.keys(contactIdsMatchCount).forEach(contactId => {
    //   let matchCount = contactIdsMatchCount[contactId];
    //   if (matchCount > highestMatchCount) { highestMatchCount = matchCount; }
    //   if (contactsByMatchCount[matchCount] == null) {
    //     contactsByMatchCount[matchCount] = [];
    //   }
    //   contactsByMatchCount[matchCount].push(matchedContactsById[contactId]);
    // });

    // let results: ContactModel[] = [];
    // for (let i = highestMatchCount; i > 0; i--) {
    //   if (contactsByMatchCount[i]) {
    //     results.pushObjects(contactsByMatchCount[i]);
    //   }
    // }

    // return results;
  }

  async filter(
    appliedFilters: AppliedPropertyFilter[] | undefined,
    {
      excludeHiddenUnlessFiltered,
    }: { excludeHiddenUnlessFiltered?: boolean } = {},
  ): Promise<ContactModel[]> {
    if (appliedFilters == null || appliedFilters.length === 0) {
      if (excludeHiddenUnlessFiltered) {
        return this.sortedContacts.filter((x) => x.active);
      } else {
        return this.sortedContacts;
      }
    }

    let showHidden = false;
    let filteredContacts = this.sortedContacts.slice();
    for (let filter of appliedFilters) {
      if (filter.propertyName === 'contact_show_hidden') {
        showHidden = true;
      }
      let filterInfo = this.availablePropertyFiltersForContacts.find(
        (x) => x.propertyName === filter.propertyName,
      )!;
      if (filterInfo.custom.preFilterQuery) {
        await filterInfo.custom.preFilterQuery(filter);
      }
      filteredContacts = (
        filterInfo.custom.filter as ContactCustomPropertyFilterFunction
      )({
        operation: filter.operation,
        val: filter.val,
        contacts: filteredContacts,
      });
    }
    if (excludeHiddenUnlessFiltered && showHidden === false) {
      filteredContacts = filteredContacts.filter((x) => x.active);
    }
    return filteredContacts;
  }

  async reloadContact(contactId: string): Promise<ContactModel> {
    let contact = this.contactsById[contactId];
    return contact.reload();
  }
}
